// Map API entity names to Vue Query cache keys for automatic invalidation.
// Handles both direct mutations (current user) and WebSocket events (other browsers).
export const API_ENTITY_QUERY_KEYS = {
    "batch-storage": [["batch-journey"]],
    product: [["products"], ["stock"], ["reports"], ["intake-tracking"]],
    // A production-run commit writes the run and Product documents. ProductsView
    // joins these two caches; invalidating only production-runs leaves new rows
    // without product weight/use-by data until a hard refresh.
    "production-run": [
        ["production-runs"],
        ["products"],
        ["stock"],
        ["production-context"],
        ["intake-lists"],
        ["intake-tracking"],
        ["reports"]
    ],
    stock: [["stock"], ["reports"]],
    "stock-operation": [
        ["stock"],
        ["stock-operations"],
        ["intake-lists"],
        ["intake-tracking"],
        ["reports"]
    ],
    user: [["users"], ["reports"], ["chat"], ["tasks"]],
    client: [["client"], ["check-forms"], ["check-schedules"], ["tasks"]],
    "temperature-log": [["temperature-logs"], ["reports"]],
    "temperature-location": [["temperature-locations"]],
    "sensor-log": [["sensor-logs"], ["monitor"]],
    audit: [["audits"]],
    check: [["checks"], ["reports"], ["check-schedules"]],
    "check-form": [["check-forms"], ["check-schedules"], ["reports"]],
    "check-schedule": [["check-schedules"], ["reports"]],
    "food-record": [["food-records"], ["food-record-items"], ["reports"]],
    "food-record-item": [["food-record-items"], ["food-records"], ["reports"]],
    location: [["locations"], ["chat"], ["tasks"]],
    department: [["departments"], ["chat"], ["tasks"]],
    supplier: [["suppliers"]],
    customer: [["customers"]],
    dispatch: [["dispatches"], ["stock"], ["products"]],
    recipe: [["recipes"], ["recipe-stock"]],
    issue: [["issues"]],
    "sensor-alert": [["sensor-alerts"], ["temperature-locations"], ["issues"]],
    complaint: [["complaints"]],
    wastage: [["wastage"], ["stock"], ["intake-lists"], ["intake-tracking"], ["reports"]],
    announcement: [["announcements"]],
    task: [["tasks"]],
    chat: [["chat"]],
    "training-course": [["training"]],
    "training-assignment": [["training"]],
    "training-record": [["training"]],
    "rota-entry": [["rota"]],
    shift: [["rota"]],
    file: [["files"]],
    "haccp-plan": [["haccp"]],
    report: [["reports"]],
    delivery: [["intake-lists"], ["stock"], ["reports"]],
    "delivery-item": [["intake-lists"], ["stock"], ["reports"]],
    meat: [["intake-lists"], ["intake-tracking"], ["stock"], ["reports"]],
    ingredient: [["intake-lists"], ["intake-tracking"], ["stock"], ["reports"]],
    "ingredient-import": [["ingredient-imports"], ["intake-lists"], ["stock"]],
    packaging: [["intake-lists"], ["intake-tracking"], ["stock"], ["reports"]],
    menu: [["menus"], ["menu-items"]],
    "menu-item": [["menu-items"], ["menus"]],
    "form-library": [["form-library"], ["check-forms"]],
    "user-clock-event": [["user-clock-events"], ["users", "statuses"], ["reports"]],
    "label-template": [["label-templates"]]
};

// Costs and recipe availability depend on all inventory sources.
for (const entity of [
    "stock",
    "ingredient",
    "ingredient-import",
    "meat",
    "packaging",
    "delivery",
    "delivery-item",
    "stock-operation",
    "wastage",
    "production-run",
    "recipe"
]) {
    for (const key of ["recipe-cost", "recipe-stock", "production-context"]) {
        if (
            !API_ENTITY_QUERY_KEYS[entity].some(
                (existing) => existing.length === 1 && existing[0] === key
            )
        )
            API_ENTITY_QUERY_KEYS[entity].push([key]);
    }
}

// The journey assembles these canonical records, including upstream batches.
for (const entity of [
    "product",
    "production-run",
    "ingredient",
    "meat",
    "packaging",
    "food-record",
    "food-record-item",
    "dispatch",
    "supplier",
    "customer"
]) {
    API_ENTITY_QUERY_KEYS[entity].push(["batch-journey"]);
}

// Entity names sorted longest-first for correct prefix matching
// (e.g., "food-record-item" must match before "food-record").
export const ENTITY_NAMES = Object.keys(API_ENTITY_QUERY_KEYS).sort((a, b) => b.length - a.length);

export function entityFromEmitterEvent(type) {
    if (typeof type !== "string") return "";

    if (type.startsWith("live-update:")) {
        return type.slice(12);
    }

    return ENTITY_NAMES.find((name) => type.startsWith(name + "-")) || "";
}

export function queryKeysForEmitterEvent(type) {
    const entity = entityFromEmitterEvent(type);
    return entity ? API_ENTITY_QUERY_KEYS[entity] || [] : [];
}

export function invalidateQueryCachesForEmitterEvent(type, queryClient) {
    queryKeysForEmitterEvent(type).forEach((queryKey) => {
        // Live chat updates must not abort a user's in-flight history page.
        // Active polling reconciles an update that arrives during that request.
        if (queryKey[0] === "chat") {
            queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
        } else {
            queryClient.invalidateQueries({ queryKey });
        }
    });
}
